import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { login } from '../services/api';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberUsername, setRememberUsername] = useState(false);
  const [keepLoggedInForToday, setKeepLoggedInForToday] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Load saved username if exists
  useEffect(() => {
    const savedUsername = localStorage.getItem('savedUsername');
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberUsername(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const response = await login(username, password);
      const { subscription_plan, password_expired, password_expiration_date } = response.data;
      
      // Save username if remember option is checked
      if (rememberUsername) {
        localStorage.setItem('savedUsername', username);
      } else {
        localStorage.removeItem('savedUsername');
      }
      
      // Store subscription plan info
      localStorage.setItem('subscriptionPlan', subscription_plan);
      
      // If "Keep me logged in for today" is checked, store the expiration time (midnight)
      if (keepLoggedInForToday) {
        const today = new Date();
        // Calculate midnight using a more reliable method to ensure timezone consistency
        // Get the next day, set hours to 0, and ensure minutes, seconds, and milliseconds are 0
        const midnight = new Date(today);
        midnight.setDate(midnight.getDate() + 1);
        midnight.setHours(0, 0, 0, 0);
        localStorage.setItem('keepLoggedInUntil', midnight.getTime().toString());
        
        // For debugging - log the expiration time
        console.log('Setting session to expire at:', new Date(midnight).toLocaleString());
      }
      
      // Store password expiration info in localStorage
      if (password_expired) {
        localStorage.setItem('passwordExpired', 'true');
        localStorage.setItem('passwordExpirationDate', password_expiration_date);
      } else if (password_expiration_date) {
        localStorage.removeItem('passwordExpired');
        localStorage.setItem('passwordExpirationDate', password_expiration_date);
      }
      
      // Pass the entire response data to onLogin instead of just token, user_id, and username
      onLogin(response.data);
    } catch (err) {
      console.error('Login error:', err);
      
      // Clear the password field for security and user convenience
      setPassword('');
      
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else if (err.response && err.response.status === 401) {
        setError('Invalid username or password. Please try again.');
      } else {
        setError('Failed to login. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center">
      <Card style={{ width: '400px' }}>
        <Card.Body>
          <Card.Title className="text-center mb-4">Login</Card.Title>
          
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="formRememberUsername">
              <Form.Check
                type="checkbox"
                label="Remember username"
                checked={rememberUsername}
                onChange={(e) => setRememberUsername(e.target.checked)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="formKeepLoggedIn">
              <Form.Check
                type="checkbox"
                label="Keep me logged in for today (until midnight)"
                checked={keepLoggedInForToday}
                onChange={(e) => setKeepLoggedInForToday(e.target.checked)}
              />
            </Form.Group>
            
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </div>
          </Form>
          
          <div className="text-center mt-3">
            <p>
              Don't have an account? <Link to="/register">Register</Link>
            </p>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default Login; 